import React from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { ApolloProvider } from '@apollo/client';
import { ReactQueryDevtools } from 'react-query/devtools';

// Components
import CartPages from './pages/Cart/CartPages';
import CompanyProfilePages from './pages/CompanyProfile/CompanyProfilePages';
import EventRegistrationPages from './pages/EventRegistration/EventRegistrationPages';
import LoginPages from './pages/Login/LoginPages';
import MemberProfilePages from './pages/MemberProfile/MemberProfilePages';
import MembershipPages from './pages/Membership/MembershipPages';
import MyIcscPages from './pages/MyIcsc/MyIcscPages';
import ErrorHandler from './_molecules/ErrorHandler';
import ProgressiveProfilingModal from './modals/ProgressiveProfilingModal';
import ScrollToTop from '@atoms/ScrollToTop';
import ViewerLoadingWrapper from '@atoms/ViewerLoadingWrapper';
import GlobalModal from '@molecules/GlobalModal';
import PageContainer from '@molecules/PageContainer';

// Functionality
import MembershipProvider from '../context/MembershipProvider';
import LoggedInProvider from '../context/LoggedInProvider';
import MessagingProviders from '../context/MessagingProviders';
import { GlobalModalContext } from '@context/globalModalContext';
import LoadingProvider from '@context/LoadingProvider';
import SearchAPIKeyProvider from '@context/SearchAPIKeyProvider';
import reactQueryClient from '@general/reactQueryClient';
import client from '@general/graphql/client';
import useModal from '@hooks/useModal';
import { routeDispatch } from '@tracking/routeDispatch';
import useIcscTracking from '@tracking/useIcscTracking';
import { StytchProvider } from '@stytch/react';
import { StytchHeadlessClient } from '@stytch/vanilla-js/headless';

if (window.aa) {
    window.aa('init', {
        appId: process.env.ALGOLIA_APP_ID,
        apiKey: process.env.ALGOLIA_API_KEY,
    });
}

const stytchClient = new StytchHeadlessClient(process.env.STYTCH_PUBLIC_KEY);

const IcscAppRoot = () => {
    const {
        isModalOpen,
        modalChildren,
        modalOuterClass,
        modalInnerClass,
        setIsModalOpen,
        setModalChildren,
        setModalOuterClass,
        setModalInnerClass,
    } = useModal();

    const { Track } = useIcscTracking(
        { app: 'Networking Platform' },
        {
            dispatch: data => routeDispatch(data),
        }
    );

    /* eslint-disable */
    return (
        <ErrorHandler>
            <Track>
                <ApolloProvider client={client}>
                    <QueryClientProvider client={reactQueryClient}>
                        <StytchProvider stytch={stytchClient}>
                            <SearchAPIKeyProvider>
                            <MessagingProviders>
                                <LoadingProvider>
                                    <GlobalModalContext.Provider
                                        value={[
                                            isModalOpen,
                                            setIsModalOpen,
                                            setModalChildren,
                                            setModalOuterClass,
                                            setModalInnerClass,
                                        ]}
                                    >
                                        <GlobalModal
                                            modalOuterClass={modalOuterClass}
                                            modalInnerClass={modalInnerClass}
                                        >
                                            {modalChildren}
                                        </GlobalModal>

                                        <Router>
                                            <ProgressiveProfilingModal />
                                            <ScrollToTop />

                                            <Switch>
                                                <Route path="/login">
                                                    <LoggedInProvider>
                                                        <PageContainer>
                                                            <LoginPages />
                                                        </PageContainer>
                                                    </LoggedInProvider>
                                                </Route>

                                                <Route path="/member/profile">
                                                    <LoggedInProvider redirectOnUnauthenticated>
                                                        <PageContainer>
                                                            <MemberProfilePages />
                                                        </PageContainer>
                                                    </LoggedInProvider>
                                                </Route>

                                                <Route path="/membership">
                                                    <ViewerLoadingWrapper>
                                                        <MembershipProvider>
                                                            <MembershipPages />
                                                        </MembershipProvider>
                                                    </ViewerLoadingWrapper>
                                                </Route>

                                                <Route path="/company/profile">
                                                    <LoggedInProvider redirectOnUnauthenticated>
                                                        <PageContainer>
                                                            <CompanyProfilePages />
                                                        </PageContainer>
                                                    </LoggedInProvider>
                                                </Route>

                                                <Route path="/event-registration/:code">
                                                    <ViewerLoadingWrapper>
                                                        <PageContainer>
                                                            <EventRegistrationPages />
                                                        </PageContainer>
                                                    </ViewerLoadingWrapper>
                                                </Route>

                                                <Route path="/cart">
                                                    <ViewerLoadingWrapper>
                                                        <PageContainer>
                                                            <CartPages />
                                                        </PageContainer>
                                                    </ViewerLoadingWrapper>
                                                </Route>

                                                <Route path="/my-icsc">
                                                    <LoggedInProvider redirectOnUnauthenticated>
                                                        <PageContainer>
                                                            <MyIcscPages />
                                                        </PageContainer>
                                                    </LoggedInProvider>
                                                </Route>
                                            </Switch>

                                            <ReactQueryDevtools initialIsOpen={false} />
                                        </Router>
                                    </GlobalModalContext.Provider>
                                </LoadingProvider>
                            </MessagingProviders>
                        </SearchAPIKeyProvider>
                        </StytchProvider>
                    </QueryClientProvider>
                </ApolloProvider>
            </Track>
        </ErrorHandler>
    );
};
IcscAppRoot.propTypes = {};

export default IcscAppRoot;
